import { zodResolver } from '@hookform/resolvers/zod';
import { FC, useContext, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { IntegrationSystem, SelectOption } from '@/shared/types';

import AuthContext from '@/contexts/Auth/AuthContext';

import { DocumentTypeEntity, StreetSelectOption } from '@/api/types';

import api from '@/api';

import {
  ProfileFormValues,
  buildFormDefaultValues,
  buildProfileUpdatePayloadForProfile,
  profileSchema,
  useIniter,
  useUpdater,
} from '../model';

import ProfilePresentational from './ProfilePresentational';

interface ProfileContainerProps {
  passportDocumentTypeEntities: Array<DocumentTypeEntity<IntegrationSystem.ERZ>> | null;
  regAddressDocumentTypeEntities: Array<DocumentTypeEntity<IntegrationSystem.ERZ>> | null;
  actualAddressDocumentTypeEntities: Array<DocumentTypeEntity<IntegrationSystem.ERZ>> | null;
}

const ProfileContainer: FC<ProfileContainerProps> = ({
  regAddressDocumentTypeEntities,
  actualAddressDocumentTypeEntities,
  passportDocumentTypeEntities,
}) => {
  const authContext = useContext(AuthContext);

  const [optionsCountry, setOptionsCountry] = useState<SelectOption[]>([]);
  const [optionsRegion, setOptionsRegion] = useState<SelectOption[]>([]);
  const [optionsDistrictReg, setOptionsDistrictReg] = useState<SelectOption[]>([]);
  const [optionsCityReg, setOptionsCityReg] = useState<SelectOption[]>([]);
  const [optionsStreetReg, setOptionsStreetReg] = useState<StreetSelectOption[]>([]);
  const [optionsDistrictLiving, setOptionsDistrictLiving] = useState<SelectOption[]>([]);
  const [optionsCityLiving, setOptionsCityLiving] = useState<SelectOption[]>([]);
  const [optionsStreetLiving, setOptionsStreetLiving] = useState<StreetSelectOption[]>([]);

  const defaultValues = useMemo(
    () =>
      buildFormDefaultValues(authContext, {
        passportDocumentTypeEntities,
        regAddressDocumentTypeEntities,
        actualAddressDocumentTypeEntities,
        optionsCountry,
        optionsRegion,
        optionsDistrictReg,
        optionsCityReg,
        optionsStreetReg,
        optionsDistrictLiving,
        optionsCityLiving,
        optionsStreetLiving,
      }),
    [
      authContext,
      passportDocumentTypeEntities,
      regAddressDocumentTypeEntities,
      actualAddressDocumentTypeEntities,
      optionsCountry,
      optionsRegion,
      optionsDistrictReg,
      optionsCityReg,
      optionsStreetReg,
      optionsDistrictLiving,
      optionsCityLiving,
      optionsStreetLiving,
    ],
  );

  const methods = useForm<ProfileFormValues>({
    resolver: zodResolver(profileSchema) as any,
    reValidateMode: 'onChange',
    shouldFocusError: false,
    defaultValues: defaultValues,
  });

  const onSubmitProfileData = async (values: ProfileFormValues): Promise<void> => {
    const newProfileData = buildProfileUpdatePayloadForProfile(values);
    try {
      const res = await api.updateUserProfile(newProfileData);
      authContext.updateUserData(res);
    } catch (error) {
      console.error(error);
    }
  };

  const { triggerReinit, reinitTriggerCount } = useIniter({
    authContext,
    regAddressDocumentTypeEntities,
    actualAddressDocumentTypeEntities,
    passportDocumentTypeEntities,
    methods,
    setOptionsCountry,
    setOptionsRegion,
    setOptionsDistrictReg,
    setOptionsCityReg,
    setOptionsStreetReg,
    setOptionsDistrictLiving,
    setOptionsCityLiving,
    setOptionsStreetLiving,
  });

  useEffect(() => {
    triggerReinit();
  }, [authContext]);

  useUpdater({
    reinitTriggerCount,
    methods,
    setOptionsDistrictReg,
    setOptionsCityReg,
    setOptionsStreetReg,
    setOptionsDistrictLiving,
    setOptionsCityLiving,
    setOptionsStreetLiving,
  });

  function onResetForm(): void {
    triggerReinit();
  }

  return (
    <FormProvider {...methods}>
      <ProfilePresentational
        reinitTriggerCount={reinitTriggerCount}
        onSubmitProfileData={onSubmitProfileData}
        onResetForm={onResetForm}
        optionsCountry={optionsCountry}
        optionsRegion={optionsRegion}
        optionsDistrictReg={optionsDistrictReg}
        optionsCityReg={optionsCityReg}
        optionsStreetReg={optionsStreetReg}
        optionsDistrictLiving={optionsDistrictLiving}
        optionsCityLiving={optionsCityLiving}
        optionsStreetLiving={optionsStreetLiving}
        passportDocumentTypeEntities={passportDocumentTypeEntities}
        regAddressDocumentTypeEntities={regAddressDocumentTypeEntities}
        actualAddressDocumentTypeEntities={actualAddressDocumentTypeEntities}
      />
    </FormProvider>
  );
};

export default ProfileContainer;
